import React, { FC, useState, useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import {
  createMarkup,
  getBackgroundColor,
  getResponsiveType,
} from '../../utils';
import { Button } from '../button';
import { Container } from '../container';
import { Menu } from '../icon';
import { LinkLabel } from '../linkLabel';
import { LinkTo } from '../linkTo/LinkTo';
import { Logo } from '../Logo/Logo';
import { Navigation } from '../navigation/Navigation';
import { ModalContext } from '../modal/ModalProvider';
import { FormRenderer } from '../componentRenderer/FormRenderer';

// ---
// Interface
// ---
export interface HeaderProps {}

// ---
// Styles
// ---
const StyledHeaderWrapper = styled.div`
  height: auto;
  position: relative;
`;

const StyledHeader = styled.header`
  ${({ theme: { media, colors } }) => css`
    width: 100%;
    background-color: ${colors.neutralWhite};
    transition: top 0.3s ease-in-out;

    @media (min-width: ${media.large}) {
      display: flex;
      flex-wrap: wrap;
    }
  `};
`;

const StyledActionBarWrapper = styled.div`
  height: auto;
  position: relative;
`;

const StyledContainer = styled(Container)`
  ${({ theme: { media } }) => css`
    width: 100%;
    padding: 0;

    @media (min-width: ${media.large}) {
      display: flex;
      flex-direction: row;
    }
  `};
`;

const StyledTopBarContainer = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    ${getBackgroundColor('neutralWhite')};
    padding: ${space.xSmall} ${space.small};

    @media (min-width: ${media.large}) {
      padding: ${space.small} 0;
    }
  `};
`;

const StyledLogo = styled(LinkTo)`
  ${({ theme: { space, layers, media } }) => css`
    margin-right: ${space.small};
    max-width: 180px;

    @media (min-width: ${media.large}) {
      z-index: ${layers.navigationTitle};
      min-width: 120px;
      max-width: 320px;
    }
  `}
`;

const StyledButtons = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    gap: ${space.xSmall};
  `};
`;

const StyledMenuButton = styled(Button)`
  ${({ theme: { colors, media } }) => css`
    min-width: 24px;

    &:hover {
      background-color: transparent;
      color: ${colors.brandOne100};
    }

    @media (min-width: ${media.large}) {
      display: none;
    }
  `};
`;

const StyledPrimaryActionBar = styled.div`
  ${({ theme: { space, colors } }) => css`
    width: 100%;
    color: ${colors.neutralWhite};
    ${getBackgroundColor('neutralGrey')};
    padding: ${space.xSmall} 0;
    transition: top 0.3s ease-in-out;
  `};
`;

const StyledActionContainer = styled(Container)`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    @media (min-width: ${media.large}) {
      flex-wrap: nowrap;
      align-items: center;
      flex-basis: 100;
      gap: ${space.xxSmall};
      justify-content: space-between;
    }
  `};
`;

const StyledLinkLabelTel = styled(LinkLabel)`
  ${() => css`
    ${getResponsiveType('headingSmall')};
    font-weight: 900;
    text-decoration: none;
  `};
`;

interface CallBackLinkProps {
  label: string;
  data: any;
  inverse: boolean;
  [key: string]: any; // Allow other props to be passed
}

const CallBackLink: FC<CallBackLinkProps> = ({ label, data, ...rest }) => {
  const { open, hide } = useContext(ModalContext);
  const { form, formGuid } =
    data?.header?.headerForm?.[0]?.block.components[0] || {};

  return (
    <LinkLabel
      onClick={() =>
        open(<FormRenderer form={form} formGuid={formGuid} hideModal={hide} />)
      }
      {...rest}
    >
      {label}
    </LinkLabel>
  );
};

// ---
// Component
// ---
export const Header: FC<HeaderProps> = () => {
  const [navOpen, setNavOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const actionBarWrapperRef = useRef<HTMLDivElement>(null);

  // Effect for handling mobile nav overlay
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;

    if (navOpen) {
      html.style.overflow = 'hidden';
      html.style.height = '100vh';
      body.style.overflow = 'hidden';
      body.style.height = '100vh';
    } else {
      html.style.overflow = 'auto';
      html.style.height = 'auto';
      body.style.overflow = 'auto';
      body.style.height = 'auto';
    }

    return () => {
      html.style.overflow = 'auto';
      html.style.height = 'auto';
      body.style.overflow = 'auto';
      body.style.height = 'auto';
    };
  }, [navOpen]);

  // Effect for handling the sticky header logic
  useEffect(() => {
    const headerWrapper = headerWrapperRef.current;
    const mainHeader = headerRef.current;
    const actionBarWrapper = actionBarWrapperRef.current;
    const actionBar = actionBarRef.current;

    if (!headerWrapper || !mainHeader || !actionBarWrapper || !actionBar)
      return;

    let scrollHandler: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let isCurrentlySticky = false;

    const setupSticky = () => {
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }

      const mainHeaderHeight = mainHeader.getBoundingClientRect().height;
      const actionBarHeight = actionBar.getBoundingClientRect().height;
      const stickyOffset = actionBarWrapper.offsetTop;

      let lastScrollY = window.scrollY;
      let ticking = false;

      scrollHandler = () => {
        const { scrollY } = window;
        const scrollDirection = scrollY > lastScrollY ? 'down' : 'up';

        if (!ticking) {
          window.requestAnimationFrame(() => {
            const shouldBeSticky = scrollY >= stickyOffset;

            if (shouldBeSticky) {
              if (!isCurrentlySticky) {
                isCurrentlySticky = true;
              }
              headerWrapper.style.height = `${mainHeaderHeight}px`;
              actionBarWrapper.style.height = `${actionBarHeight}px`;

              actionBar.style.position = 'fixed';
              actionBar.style.left = '0';
              actionBar.style.right = '0';
              actionBar.style.zIndex = '8';
              actionBar.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.1)';

              mainHeader.style.position = 'fixed';
              mainHeader.style.left = '0';
              mainHeader.style.right = '0';
              mainHeader.style.zIndex = '9';
              mainHeader.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.1)';

              if (scrollDirection === 'up') {
                mainHeader.style.top = '0';
                actionBar.style.top = `${mainHeaderHeight}px`;
              } else {
                mainHeader.style.top = `-${mainHeaderHeight}px`;
                actionBar.style.top = '0';
              }
            } else if (isCurrentlySticky) {
              if (scrollDirection === 'up' && scrollY <= 0) {
                isCurrentlySticky = false;

                headerWrapper.style.height = 'auto';
                mainHeader.style.position = '';
                mainHeader.style.top = '';
                mainHeader.style.left = '';
                mainHeader.style.right = '';
                mainHeader.style.zIndex = '';
                mainHeader.style.boxShadow = '';

                actionBarWrapper.style.height = 'auto';
                actionBar.style.position = '';
                actionBar.style.top = '';
                actionBar.style.left = '';
                actionBar.style.right = '';
                actionBar.style.zIndex = '';
                actionBar.style.boxShadow = '';
              }
            }

            ticking = false;
          });
          ticking = true;
        }
        lastScrollY = scrollY <= 0 ? 0 : scrollY;
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
    };

    resizeObserver = new ResizeObserver(() => {
      setupSticky();
    });

    resizeObserver.observe(headerWrapper);

    if (document.readyState === 'complete') {
      setupSticky();
    } else {
      window.addEventListener('load', setupSticky);
    }

    return () => {
      window.removeEventListener('load', setupSticky);
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const data = useStaticQuery(graphql`
    query HeaderQuery {
      navigation: umbracoHeaderType {
        navigation {
          block {
            components {
              ... on UMBRACO_NavigationComponentType {
                link {
                  linkName
                  url
                }
                childNavigation {
                  link {
                    linkName
                    url
                  }
                }
              }
            }
          }
        }
      }
      header: umbracoHeaderType {
        id
        callToActionText
        headerForm {
          block {
            components {
              name
              ... on UMBRACO_FormComponentType {
                ...Form
              }
            }
          }
        }
      }
    }
  `);

  return (
    <>
      <StyledHeaderWrapper ref={headerWrapperRef}>
        <StyledHeader ref={headerRef}>
          <StyledContainer>
            <StyledTopBarContainer>
              <StyledLogo to="/" title="trade direct insurance">
                <Logo />
              </StyledLogo>
              <StyledButtons>
                <StyledMenuButton
                  variant="primaryInverse"
                  iconRight={<Menu size="small" />}
                  a11yTitle="Open menu"
                  onClick={() => setNavOpen(true)}
                  small
                />
              </StyledButtons>
            </StyledTopBarContainer>

            <Navigation
              isActive={navOpen}
              items={data?.navigation?.navigation?.[0].block?.components}
              setNavOpen={setNavOpen}
            />
          </StyledContainer>
        </StyledHeader>
      </StyledHeaderWrapper>
      <StyledActionBarWrapper ref={actionBarWrapperRef}>
        <StyledPrimaryActionBar ref={actionBarRef}>
          <StyledActionContainer>
            {data?.header?.callToActionText && (
              <div>{createMarkup(data.header.callToActionText)}</div>
            )}
            <div>
              <StyledLinkLabelTel
                url="tel:0800 0280 380"
                label="0800 0280 380"
                inverse
              />
              {` or `}
              <CallBackLink label=" arrange a call-back" inverse data={data} />
            </div>
          </StyledActionContainer>
        </StyledPrimaryActionBar>
      </StyledActionBarWrapper>
    </>
  );
};

export default Header;
